import { useState, useRef, Fragment } from "react";
import { MenuItem, Checkbox, Tooltip } from "@mui/material";
import { CoreSelect, CoreButton } from "@verity/components/core";
import AddIcon from "@mui/icons-material/Add";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import i18n from "../../../../i18n";
import {
	baseTransKey,
	MatchingDistractor,
	getDefaultQuestionPatch,
	getOptionLetter,
	QuizQuestionType,
	QuizDraftQuestion,
} from "./verityAssessmentHelper";
import styles from "./QuestionEditor.module.css";
import QuestionRichTextEditor from "./QuestionRichTextEditor";
import type { QuestionRichTextEditorHandle } from "./QuestionRichTextEditor";

import MultipleChoiceIcon from "../../../../images/quiz-question/question-type-icon-multiple-choice.svg?react";
import TrueFalseIcon from "../../../../images/quiz-question/question-type-icon-true-false.svg?react";
import MatchingIcon from "../../../../images/quiz-question/question-type-icon-matching.svg?react";
import FillInTheBlankIcon from "../../../../images/quiz-question/question-type-icon-fill-in-the-blank.svg?react";
import PointsIcon from "../../../../images/quiz-question/points-icon.svg?react";
import CheckIcon from "../../../../images/check-pass.svg?react";
import CorrectAnswerCheckedIcon from "../../../../images/quiz-question/option-correct-answer-checked-icon.svg?react";
import DrageIcon from "../../../../images/quiz-question/option-drag-icon.svg?react";
import MatchingArrowIcon from "../../../../images/quiz-question/matching-arrow-right-icon.svg?react";
import DeleteIcon from "../../../../images/delete.svg?react";
import {
	FITBBlankOptionInstructor,
	MatchingPair,
	MatchingPairInstructor,
	MCOptionInstructor,
	TFOptionInstructor,
} from "@verity/api-client/types";

const QUESTION_TYPE_LIST: {
	type: QuizQuestionType;
	Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}[] = [
	{ type: "MultipleChoice", Icon: MultipleChoiceIcon },
	{ type: "TrueFalse", Icon: TrueFalseIcon },
	{ type: "Matching", Icon: MatchingIcon },
	{ type: "FillInTheBlank", Icon: FillInTheBlankIcon },
];

const TYPE_ICON_MAP: Record<QuizQuestionType, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
	MultipleChoice: MultipleChoiceIcon,
	TrueFalse: TrueFalseIcon,
	Matching: MatchingIcon,
	FillInTheBlank: FillInTheBlankIcon,
};

const PRESET_POINTS = [1, 3, 5];
const MAX_QUESTION_LENGTH = 1000;

function getPointsLabel(count: number): string {
	if (count === 0) return i18n.t(`${baseTransKey}.questions.editor.ungraded`) as string;
	return i18n.t(`${baseTransKey}.questions.editor.correctionPoints`, { count }) as string;
}

export default function QuestionEditor({
	question,
	onQuestionChange,
}: {
	question: QuizDraftQuestion & { questionType: QuizQuestionType };
	onQuestionChange: (q: QuizDraftQuestion) => void;
}) {
	const [isCustomPoints, setIsCustomPoints] = useState(false);
	const [customPointsInput, setCustomPointsInput] = useState(String(question.correctionPoints));
	const [isMatchingRightDragging, setIsMatchingRightDragging] = useState(false);
	const [focusedInputKey, setFocusedInputKey] = useState<string | null>(null);
	const fitbEditorRef = useRef<QuestionRichTextEditorHandle>(null);

	const options = (question.options as Array<MCOptionInstructor | TFOptionInstructor> | undefined) ?? [];
	const fitbBlanks = (question.options as FITBBlankOptionInstructor[] | undefined) ?? [];
	const matchingAnswers = (question.options as Array<MatchingPair | MatchingDistractor> | undefined) ?? [];
	const matchingPairs = matchingAnswers.filter((a): a is MatchingPair => "premise" in a);
	const matchingDistractors = matchingAnswers.filter((a): a is MatchingDistractor => !("premise" in a));

	const handleTypeChange = (type: QuizQuestionType) => {
		// Strip blank spans if switching away from FITB
		const strippedText =
			question.questionType === "FillInTheBlank"
				? question.questionText.replace(/<span[^>]*class="question-blank"[^>]*>[\s\S]*?<\/span>[\s\S]*?<\/span>/g, "")
				: question.questionText;
		const patch = getDefaultQuestionPatch(type);
		// Only apply patch's starter questionText if current text is effectively empty
		const parsedText = new DOMParser().parseFromString(strippedText, "text/html").body.textContent ?? "";
		const isTextEmpty = !parsedText.trim();
		onQuestionChange({
			...question,
			questionType: type,
			questionText:
				(isTextEmpty || type === "FillInTheBlank") && patch.questionText ? patch.questionText : strippedText,
			options: patch.options,
		});
	};

	const handlePointsSelectChange = (value: number | "custom") => {
		if (value === "custom") {
			setIsCustomPoints(true);
		} else {
			setIsCustomPoints(false);
			onQuestionChange({ ...question, correctionPoints: value });
		}
	};

	const handleCustomPointsConfirm = () => {
		const parsed = Math.max(0, Math.min(1000, parseInt(customPointsInput, 10)));
		if (!isNaN(parsed) && parsed >= 0) {
			onQuestionChange({ ...question, correctionPoints: parsed });
		}
		setIsCustomPoints(false);
	};

	const handleCustomPointsBlur = () => {
		const parsed = Math.max(0, Math.min(1000, parseInt(customPointsInput, 10)));
		if (!isNaN(parsed) && parsed >= 0) {
			onQuestionChange({ ...question, correctionPoints: parsed });
		}
		setIsCustomPoints(false);
	};

	const handleAddOption = () => {
		const newOptions: MCOptionInstructor[] = [...options, { text: "", weightage: 0 }];
		onQuestionChange({ ...question, options: newOptions });
	};

	const handleCorrectToggle = (index: number) => {
		if (question.questionType === "TrueFalse") {
			// Only one answer can be correct; clicking the already-correct one deselects it
			const newOptions = options.map((o, i) => ({
				...o,
				weightage: i === index && o.weightage === 0 ? 1 : 0,
			}));
			onQuestionChange({ ...question, options: newOptions });
		} else if (question.questionType === "MultipleChoice") {
			// Single correct answer for now; weightage structure kept for future multi-correct support
			const newOptions = options.map((o, i) => ({
				...o,
				weightage: i === index && o.weightage === 0 ? 1 : 0,
			}));
			onQuestionChange({ ...question, options: newOptions });
		}
	};

	const handleDeleteOption = (index: number) => {
		const newOptions = options.filter((_, i) => i !== index);
		onQuestionChange({ ...question, options: newOptions });
	};

	const handleOptionTextChange = (index: number, text: string) => {
		const newOptions = options.map((o, i) => (i === index ? { ...o, text } : o));
		onQuestionChange({ ...question, options: newOptions });
	};

	const handleDragEnd = (result: DropResult) => {
		if (!result.destination) return;
		if (result.destination.index === result.source.index) return;
		const newOptions = Array.from(options);
		const [removed] = newOptions.splice(result.source.index, 1);
		newOptions.splice(result.destination.index, 0, removed);
		onQuestionChange({ ...question, options: newOptions });
	};

	function parseBlankIdsFromHtml(html: string): number[] {
		const matches = [...html.matchAll(/data-id="(\d+)"/g)];
		return [...new Set(matches.map((m) => parseInt(m[1], 10)))];
	}

	const handleFitbTextChange = (html: string) => {
		const blankIdsInText = parseBlankIdsFromHtml(html);
		const currentBlanks = (question.options as FITBBlankOptionInstructor[] | undefined) ?? [];
		const totalBlanks = blankIdsInText.length;
		const weightage = totalBlanks > 0 ? Math.round((1 / totalBlanks) * 100) / 100 : 0;
		const syncedBlanks = blankIdsInText.map((id) => {
			const existing = currentBlanks.find((a) => a.blankId === id);
			return existing ? { ...existing, weightage } : { blankId: id, answers: [""], weightage };
		});
		onQuestionChange({ ...question, questionText: html, options: syncedBlanks });
	};

	const handleAddBlank = () => {
		const currentBlanks = (question.options as FITBBlankOptionInstructor[] | undefined) ?? [];
		const nextId = currentBlanks.length > 0 ? Math.max(...currentBlanks.map((b) => b.blankId)) + 1 : 1;
		fitbEditorRef.current?.insertBlank(nextId);
	};

	const handleBlankAnswerChange = (blankId: number, answerIdx: number, text: string) => {
		const newBlanks = fitbBlanks.map((b) =>
			b.blankId === blankId ? { ...b, answers: b.answers.map((a, i) => (i === answerIdx ? text : a)) } : b,
		);
		onQuestionChange({ ...question, options: newBlanks });
	};

	const handleAddBlankAnswer = (blankId: number) => {
		const newBlanks = fitbBlanks.map((b) => (b.blankId === blankId ? { ...b, answers: [...b.answers, ""] } : b));
		onQuestionChange({ ...question, options: newBlanks });
	};

	const handleDeleteBlankAnswer = (blankId: number, answerIdx: number) => {
		const newBlanks = fitbBlanks.map((b) =>
			b.blankId === blankId ? { ...b, answers: b.answers.filter((_, i) => i !== answerIdx) } : b,
		);
		onQuestionChange({ ...question, options: newBlanks });
	};

	// ── Matching handlers ────────────────────────────────────
	const calcMatchingWeightage = (count: number) => (count > 0 ? Math.round((1 / count) * 100) / 100 : 0);

	const updateMatchingAnswers = (pairs: MatchingPair[], distractors: MatchingDistractor[]) => {
		onQuestionChange({ ...question, options: [...pairs, ...distractors] as Array<MatchingPairInstructor> });
	};

	const handleMatchingPremiseDragEnd = (result: DropResult) => {
		if (!result.destination || result.destination.index === result.source.index) return;
		// Only reorder premise texts; responses stay in their original slots
		const premises = matchingPairs.map((p) => p.premise);
		const [moved] = premises.splice(result.source.index, 1);
		premises.splice(result.destination.index, 0, moved);
		const newPairs = matchingPairs.map((p, i) => ({ ...p, premise: premises[i] }));
		updateMatchingAnswers(newPairs, matchingDistractors);
	};

	const handleMatchingRightDragEnd = (result: DropResult) => {
		setIsMatchingRightDragging(false);
		if (!result.destination || result.destination.index === result.source.index) return;
		// Build a flat list of all response texts (correct first, then distractors)
		const allResponses = [...matchingPairs.map((p) => p.response), ...matchingDistractors.map((d) => d.response)];
		const [moved] = allResponses.splice(result.source.index, 1);
		allResponses.splice(result.destination.index, 0, moved);
		// Re-split: first N slots → correct responses, rest → distractors
		const newPairs = matchingPairs.map((p, i) => ({ ...p, response: allResponses[i] }));
		const newDistractors = matchingDistractors.map((d, i) => ({
			...d,
			response: allResponses[matchingPairs.length + i],
		}));
		updateMatchingAnswers(newPairs, newDistractors);
	};

	const handleAddMatchingPair = () => {
		const newCount = matchingPairs.length + 1;
		const w = calcMatchingWeightage(newCount);
		const newPairs: MatchingPair[] = [
			...matchingPairs.map((p) => ({ ...p, weightage: w })),
			{ premise: "", response: "", weightage: w },
		];
		updateMatchingAnswers(newPairs, matchingDistractors);
	};

	const handleDeleteMatchingPair = (idx: number) => {
		const newPairs = matchingPairs.filter((_, i) => i !== idx);
		const w = calcMatchingWeightage(newPairs.length);
		updateMatchingAnswers(
			newPairs.map((p) => ({ ...p, weightage: w })),
			matchingDistractors,
		);
	};

	const handleAddDistractor = () => {
		updateMatchingAnswers(matchingPairs, [...matchingDistractors, { response: "", weightage: 0 }]);
	};

	const handleDeleteDistractor = (idx: number) => {
		updateMatchingAnswers(
			matchingPairs,
			matchingDistractors.filter((_, i) => i !== idx),
		);
	};

	const handleMatchingPremiseChange = (idx: number, text: string) => {
		updateMatchingAnswers(
			matchingPairs.map((p, i) => (i === idx ? { ...p, premise: text } : p)),
			matchingDistractors,
		);
	};

	const handleMatchingResponseChange = (idx: number, text: string) => {
		updateMatchingAnswers(
			matchingPairs.map((p, i) => (i === idx ? { ...p, response: text } : p)),
			matchingDistractors,
		);
	};

	const handleMatchingDistractorChange = (idx: number, text: string) => {
		updateMatchingAnswers(
			matchingPairs,
			matchingDistractors.map((d, i) => (i === idx ? { ...d, response: text } : d)),
		);
	};

	const pointsSelectValue: number | "custom" = isCustomPoints
		? "custom"
		: PRESET_POINTS.includes(question.correctionPoints)
			? question.correctionPoints
			: "custom";

	return (
		<div className={styles.container}>
			{/* ── Row 1: Header bar ── */}
			<div className={styles.headerBar}>
				<div className={styles.typeSelectWrapper}>
					<CoreSelect<QuizQuestionType>
						value={question.questionType}
						onChange={(e) => handleTypeChange(e.target.value as QuizQuestionType)}
						size="small"
						aria-label={i18n.t(`${baseTransKey}.questions.editor.questionTypeLabel`) as string}
						renderValue={(val) => {
							const Icon = TYPE_ICON_MAP[val];
							return (
								<div className={styles.typeSelectValue}>
									{Icon && (
										<div className={styles.typeSelectIconWrapper}>
											<Icon className={styles.typeSelectIcon} />
										</div>
									)}
									<span>{i18n.t(`${baseTransKey}.questions.${val}.label`)}</span>
								</div>
							);
						}}
					>
						{QUESTION_TYPE_LIST.map(({ type, Icon }) => (
							<MenuItem key={type} value={type}>
								<div className={styles.typeMenuItem}>
									<div className={styles.typeSelectIconWrapper}>
										<Icon className={styles.typeSelectIcon} />
									</div>
									<span>{i18n.t(`${baseTransKey}.questions.${type}.label`)}</span>
								</div>
							</MenuItem>
						))}
					</CoreSelect>
				</div>

				<div className={styles.pointsWrapper}>
					{isCustomPoints ? (
						<div className={styles.customPointsBox}>
							<div className={styles.typeSelectIconWrapper}>
								<PointsIcon className={styles.pointsSelectIcon} />
							</div>
							<input
								type="number"
								min={0}
								max={1000}
								className={styles.customPointsInput}
								value={customPointsInput}
								onChange={(e) => setCustomPointsInput(e.target.value)}
								onBlur={handleCustomPointsBlur}
								onKeyDown={(e) => {
									if (e.key === "Enter") e.currentTarget.blur();
								}}
								aria-label={i18n.t(`${baseTransKey}.questions.editor.customPointsLabel`) as string}
								autoFocus
							/>
							<button
								className={styles.customPointsConfirm}
								onMouseDown={(e) => e.preventDefault()}
								onClick={handleCustomPointsConfirm}
								aria-label={i18n.t(`${baseTransKey}.questions.editor.customPointsLabel`) as string}
							>
								<CheckIcon />
							</button>
						</div>
					) : (
						<CoreSelect<number | string>
							value={pointsSelectValue}
							onChange={(e) => handlePointsSelectChange(e.target.value as number | "custom")}
							size="small"
							aria-label={i18n.t(`${baseTransKey}.questions.editor.pointsLabel`) as string}
							renderValue={() => (
								<div className={styles.pointsSelectValue}>
									<div className={styles.typeSelectIconWrapper}>
										<PointsIcon className={styles.pointsSelectIcon} />
									</div>

									<span>{getPointsLabel(question.correctionPoints)}</span>
								</div>
							)}
						>
							{PRESET_POINTS.map((p) => (
								<MenuItem key={p} value={p} className={styles.pointsMenuItem}>
									{getPointsLabel(p)}
								</MenuItem>
							))}
							<MenuItem value="custom" className={styles.pointsMenuItem} onClick={() => setIsCustomPoints(true)}>
								{i18n.t(`${baseTransKey}.questions.editor.custom`)}
							</MenuItem>
						</CoreSelect>
					)}
				</div>
			</div>

			{/* ── Row 2: Question text editor ── */}
			<QuestionRichTextEditor
				ref={fitbEditorRef}
				value={question.questionText}
				onChange={
					question.questionType === "FillInTheBlank"
						? handleFitbTextChange
						: (text) => onQuestionChange({ ...question, questionText: text })
				}
				placeholder={i18n.t(`${baseTransKey}.questions.editor.questionPlaceholder`) as string}
				maxLength={MAX_QUESTION_LENGTH}
				toolbarEnd={
					question.questionType === "FillInTheBlank" ? (
						<CoreButton
							component="div"
							onMouseDown={(e) => e.preventDefault()}
							onClick={handleAddBlank}
							className={styles.addBlankButton}
						>
							{i18n.t(`${baseTransKey}.questions.editor.addBlank`)}
						</CoreButton>
					) : undefined
				}
			/>

			{/* ── Row 3: Options ── */}
			{question.questionType === "MultipleChoice" && (
				<div className={styles.optionsContainer}>
					<DragDropContext onDragEnd={handleDragEnd}>
						<Droppable droppableId="options">
							{(droppableProvided) => (
								<div
									ref={droppableProvided.innerRef}
									{...droppableProvided.droppableProps}
									className={styles.optionsList}
								>
									{options.map((opt, idx) => (
										<Draggable key={`option-${idx}`} draggableId={`option-${idx}`} index={idx}>
											{(draggableProvided) => (
												<div
													className={styles.optionWrapper}
													ref={draggableProvided.innerRef}
													{...draggableProvided.draggableProps}
												>
													<div className={styles.optionRow}>
														{question.correctionPoints > 0 ? (
															<div
																className={styles.optionCheckBoxContainer}
																data-correct={opt.weightage > 0 ? "" : undefined}
															>
																<Checkbox
																	checked={opt.weightage > 0}
																	onChange={() => handleCorrectToggle(idx)}
																	inputProps={{ "aria-label": "Mark as correct answer" }}
																	className={styles.optionCorrectCheckbox}
																	icon={<span className={styles.optionCheckboxIcon} />}
																	disableRipple
																	checkedIcon={
																		<span
																			className={`${styles.optionCheckboxIcon} ${styles.optionCheckboxIconChecked}`}
																		>
																			<CorrectAnswerCheckedIcon className={styles.optionCorrectCheckboxIcon} />
																		</span>
																	}
																/>
															</div>
														) : (
															<div></div>
														)}

														<span className={styles.optionDragHandle} {...draggableProvided.dragHandleProps}>
															<DrageIcon />
														</span>
														<span
															className={`${styles.optionLetter} ${opt.weightage > 0 && question.correctionPoints > 0 ? styles.optionLetterCorrect : ""}`}
														>
															{getOptionLetter(idx)}
														</span>
														<div className={styles.optionInputWrapper}>
															<input
																className={styles.optionInput}
																value={opt.text}
																maxLength={100}
																onChange={(e) => handleOptionTextChange(idx, e.target.value)}
																placeholder={`${i18n.t(`${baseTransKey}.questions.editor.optionLabel`)} ${getOptionLetter(idx)}`}
																onFocus={() => setFocusedInputKey(`option-${idx}`)}
																onBlur={() => setFocusedInputKey(null)}
															/>
															{focusedInputKey === `option-${idx}` && opt.text.length > 0 && (
																<span className={styles.optionCharCount}>{opt.text.length}/100</span>
															)}
														</div>
														{options.length > 2 && (
															<button
																className={styles.optionDeleteBtn}
																onClick={() => handleDeleteOption(idx)}
																aria-label={i18n.t(`${baseTransKey}.questions.editor.deleteOptionLabel`) as string}
															>
																<DeleteIcon className={styles.deleteIcon} />
															</button>
														)}
													</div>
												</div>
											)}
										</Draggable>
									))}
									{droppableProvided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
					<CoreButton startIcon={<AddIcon />} onClick={handleAddOption} className={styles.addOptionButton}>
						{i18n.t(`${baseTransKey}.questions.editor.addOption`)}
					</CoreButton>
				</div>
			)}

			{question.questionType === "TrueFalse" &&
				(() => {
					const tfOptions: TFOptionInstructor[] =
						options.length === 2
							? (options as TFOptionInstructor[])
							: [
									{ text: "True", weightage: 0 },
									{ text: "False", weightage: 0 },
								];
					return (
						<div className={styles.optionsContainer}>
							<div className={styles.optionsList}>
								{tfOptions.map((opt, idx) => (
									<div key={idx} className={styles.optionWrapper}>
										<div className={styles.optionRow}>
											{question.correctionPoints > 0 ? (
												<div
													className={styles.optionCheckBoxContainer}
													data-correct={opt.weightage > 0 ? "" : undefined}
												>
													<Checkbox
														checked={opt.weightage > 0}
														onChange={() => handleCorrectToggle(idx)}
														inputProps={{ "aria-label": "Mark as correct answer" }}
														className={styles.optionCorrectCheckbox}
														icon={<span className={styles.optionCheckboxIcon} />}
														disableRipple
														checkedIcon={
															<span className={`${styles.optionCheckboxIcon} ${styles.optionCheckboxIconChecked}`}>
																<CorrectAnswerCheckedIcon className={styles.optionCorrectCheckboxIcon} />
															</span>
														}
													/>
												</div>
											) : (
												<div></div>
											)}
											<span className={styles.optionTfLabel}>{opt.text}</span>
										</div>
									</div>
								))}
							</div>
						</div>
					);
				})()}

			{question.questionType === "Matching" && (
				<div className={styles.matchingContainer}>
					{/* ── Left column: Premises ── */}
					<div className={styles.matchingLeft}>
						<span className={styles.matchingColumnHeader}>
							{i18n.t(`${baseTransKey}.questions.editor.matching.premise`)}
						</span>
						<DragDropContext onDragEnd={handleMatchingPremiseDragEnd}>
							<Droppable droppableId="matching-premises">
								{(droppableProvided) => (
									<div
										ref={droppableProvided.innerRef}
										{...droppableProvided.droppableProps}
										className={styles.matchingList}
									>
										{matchingPairs.map((pair, idx) => (
											<Draggable key={`premise-${idx}`} draggableId={`premise-${idx}`} index={idx}>
												{(draggableProvided) => (
													<div
														className={styles.matchingItem}
														ref={draggableProvided.innerRef}
														{...draggableProvided.draggableProps}
													>
														<div></div>
														<span className={styles.optionDragHandle} {...draggableProvided.dragHandleProps}>
															<DrageIcon />
														</span>
														<span className={styles.matchingIndex}>{idx + 1}</span>
														<div className={styles.optionInputWrapper}>
															<input
																className={styles.matchingInput}
																value={pair.premise}
																onChange={(e) => handleMatchingPremiseChange(idx, e.target.value)}
																placeholder={
																	i18n.t(`${baseTransKey}.questions.editor.matching.premisePlaceholder`, {
																		index: idx + 1,
																	}) as string
																}
																maxLength={100}
																onFocus={() => setFocusedInputKey(`premise-${idx}`)}
																onBlur={() => setFocusedInputKey(null)}
															/>
															{focusedInputKey === `premise-${idx}` && pair.premise.length > 0 && (
																<span className={styles.optionCharCount}>{pair.premise.length}/100</span>
															)}
														</div>
													</div>
												)}
											</Draggable>
										))}
										{droppableProvided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>
						<CoreButton startIcon={<AddIcon />} onClick={handleAddMatchingPair} className={styles.matchingAddBtn}>
							{i18n.t(`${baseTransKey}.questions.editor.matching.addMatchingPair`)}
						</CoreButton>
					</div>

					{/* ── Middle column: Arrows (one per pair) ── */}
					<div className={styles.matchingMiddle}>
						<span className={styles.matchingColumnHeaderSpacer} />
						<div className={styles.matchingArrowList}>
							{matchingPairs.map((_, idx) => (
								<span key={idx} className={styles.matchingArrow}>
									<MatchingArrowIcon />
								</span>
							))}
						</div>
					</div>

					{/* ── Right column: All responses (correct + distractor) in one list ── */}
					<div className={styles.matchingRight}>
						<span className={styles.matchingColumnHeader}>
							{i18n.t(`${baseTransKey}.questions.editor.matching.correctResponse`)}
						</span>
						<DragDropContext
							onDragStart={() => setIsMatchingRightDragging(true)}
							onDragEnd={handleMatchingRightDragEnd}
						>
							<Droppable droppableId="matching-right">
								{(droppableProvided) => (
									<div
										ref={droppableProvided.innerRef}
										{...droppableProvided.droppableProps}
										className={styles.matchingList}
									>
										{/* Correct responses */}
										{matchingPairs.map((pair, idx) => (
											<Draggable key={`right-${idx}`} draggableId={`right-${idx}`} index={idx}>
												{(draggableProvided) => (
													<div
														className={styles.matchingItem}
														ref={draggableProvided.innerRef}
														{...draggableProvided.draggableProps}
													>
														<div></div>
														<span className={styles.optionDragHandle} {...draggableProvided.dragHandleProps}>
															<DrageIcon />
														</span>
														<div className={styles.optionInputWrapper}>
															<input
																className={styles.matchingInput}
																value={pair.response}
																onChange={(e) => handleMatchingResponseChange(idx, e.target.value)}
																placeholder={
																	i18n.t(`${baseTransKey}.questions.editor.matching.responsePlaceholder`, {
																		index: idx + 1,
																	}) as string
																}
																maxLength={100}
																onFocus={() => setFocusedInputKey(`response-${idx}`)}
																onBlur={() => setFocusedInputKey(null)}
															/>
															{focusedInputKey === `response-${idx}` && pair.response.length > 0 && (
																<span className={styles.optionCharCount}>{pair.response.length}/100</span>
															)}
														</div>
														{matchingPairs.length > 2 && (
															<button
																className={styles.optionDeleteBtn}
																onClick={() => handleDeleteMatchingPair(idx)}
																aria-label={i18n.t(`${baseTransKey}.questions.editor.matching.deletePair`) as string}
															>
																<DeleteIcon className={styles.deleteIcon} />
															</button>
														)}
													</div>
												)}
											</Draggable>
										))}
										{/* Distractors */}
										{matchingDistractors.length > 0 && !isMatchingRightDragging && (
											<div className={styles.matchingDistractorDivider}>
												<span className={styles.matchingDistractorHeader}>
													{i18n.t(`${baseTransKey}.questions.editor.matching.distractor`)}
												</span>
											</div>
										)}
										{matchingDistractors.map((distractor, idx) => (
											<Draggable
												key={`right-${matchingPairs.length + idx}`}
												draggableId={`right-${matchingPairs.length + idx}`}
												index={matchingPairs.length + idx}
											>
												{(draggableProvided) => (
													<div
														className={styles.matchingItem}
														ref={draggableProvided.innerRef}
														{...draggableProvided.draggableProps}
													>
														<div></div>
														<span className={styles.optionDragHandle} {...draggableProvided.dragHandleProps}>
															<DrageIcon />
														</span>
														<div className={styles.optionInputWrapper}>
															<input
																className={styles.matchingInput}
																value={distractor.response}
																onChange={(e) => handleMatchingDistractorChange(idx, e.target.value)}
																placeholder={
																	i18n.t(`${baseTransKey}.questions.editor.matching.responsePlaceholder`, {
																		index: matchingPairs.length + idx + 1,
																	}) as string
																}
																maxLength={100}
																onFocus={() => setFocusedInputKey(`distractor-${idx}`)}
																onBlur={() => setFocusedInputKey(null)}
															/>
															{focusedInputKey === `distractor-${idx}` && distractor.response.length > 0 && (
																<span className={styles.optionCharCount}>{distractor.response.length}/100</span>
															)}
														</div>
														<button
															className={styles.optionDeleteBtn}
															onClick={() => handleDeleteDistractor(idx)}
															aria-label={
																i18n.t(`${baseTransKey}.questions.editor.matching.deleteDistractor`) as string
															}
														>
															<DeleteIcon className={styles.deleteIcon} />
														</button>
													</div>
												)}
											</Draggable>
										))}
										{droppableProvided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>
						<CoreButton startIcon={<AddIcon />} onClick={handleAddDistractor} className={styles.matchingAddBtn}>
							{i18n.t(`${baseTransKey}.questions.editor.matching.addDistractor`)}
						</CoreButton>
					</div>
				</div>
			)}

			{question.questionType === "FillInTheBlank" && fitbBlanks.length > 0 && question.correctionPoints > 0 && (
				<>
					<span className={styles.fitbSectionTitle}>
						{i18n.t(`${baseTransKey}.questions.editor.fitb.correctAnswers`)}
					</span>
					{fitbBlanks.map((blank) => (
						<Fragment key={blank.blankId}>
							<div className={styles.fitbBlankGroup}>
								<div className={styles.fitbBlankLabel}>
									{i18n.t(`${baseTransKey}.questions.editor.fitb.blankLabel`, { id: blank.blankId })}
								</div>
								<div className={styles.fitbAnswersList}>
									{blank.answers.map((answer, answerIdx) => (
										<div key={answerIdx} className={styles.fitbAnswerRow}>
											<div className={styles.optionInputWrapper}>
												<input
													className={styles.fitbAnswerInput}
													value={answer}
													onChange={(e) => handleBlankAnswerChange(blank.blankId, answerIdx, e.target.value)}
													placeholder={i18n.t(`${baseTransKey}.questions.editor.fitb.answerPlaceholder`) as string}
													maxLength={100}
													onFocus={() => setFocusedInputKey(`fitb-${blank.blankId}-${answerIdx}`)}
													onBlur={() => setFocusedInputKey(null)}
												/>
												{focusedInputKey === `fitb-${blank.blankId}-${answerIdx}` && answer.length > 0 && (
													<span className={styles.optionCharCount}>{answer.length}/100</span>
												)}
											</div>

											{blank.answers.length > 1 && (
												<Tooltip title={i18n.t(`${baseTransKey}.questions.editor.fitb.deleteAnswer`) as string}>
													<button
														className={styles.fitbAnswerDeleteBtn}
														onClick={() => handleDeleteBlankAnswer(blank.blankId, answerIdx)}
														disabled={blank.answers.length <= 1}
														aria-label={i18n.t(`${baseTransKey}.questions.editor.fitb.deleteAnswer`) as string}
													>
														<DeleteIcon className={styles.deleteIcon} />
													</button>
												</Tooltip>
											)}
										</div>
									))}
								</div>
							</div>

							<CoreButton
								startIcon={<AddIcon />}
								className={styles.fitbAddAnswerBtn}
								onClick={() => handleAddBlankAnswer(blank.blankId)}
							>
								{i18n.t(`${baseTransKey}.questions.editor.fitb.addAnotherAnswer`)}
							</CoreButton>
						</Fragment>
					))}
				</>
			)}
			{/* 
			<div>{JSON.stringify(question)}</div> */}
		</div>
	);
}
